export const HOSPITAL_TIME_ZONE = "America/Aruba";

export type HospitalDateParts = {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
};

const partsFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: HOSPITAL_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
});

function pad(value: number) {
    return String(value).padStart(2, "0");
}

export function getHospitalDateParts(value: Date | string): HospitalDateParts | null {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;

    const parts = Object.fromEntries(partsFormatter.formatToParts(date).filter((part) => part.type !== "literal").map((part) => [part.type, Number(part.value)]));
    return { year: parts.year, month: parts.month, day: parts.day, hour: parts.hour, minute: parts.minute, second: parts.second };
}

export function getHospitalDateKey(value: Date | string) {
    const parts = getHospitalDateParts(value);
    return parts ? `${parts.year}-${pad(parts.month)}-${pad(parts.day)}` : "";
}

function hospitalDateTimeToInstant(year: number, month: number, day: number, hour = 0, minute = 0, second = 0) {
    const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
    const parts = getHospitalDateParts(utcGuess);
    if (!parts) return null;
    const offsetMS = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second) - utcGuess.getTime();
    return new Date(utcGuess.getTime() - offsetMS);
}

export function parseHospitalDate(value: string) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (!match) return null;
    return hospitalDateTimeToInstant(Number(match[1]), Number(match[2]), Number(match[3]));
}

export function parseHospitalDateTime(value: string) {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value.trim());
    if (!match) return null;
    return hospitalDateTimeToInstant(Number(match[1]), Number(match[2]), Number(match[3]), Number(match[4]), Number(match[5]), Number(match[6] ?? 0));
}

export function getHospitalDateBoundary(dateKey: string, dayOffset = 0, hour = 0) {
    const date = parseHospitalDate(dateKey);
    return date ? new Date(date.getTime() + (dayOffset * 24 + hour) * 60 * 60 * 1000) : null;
}

export function formatHospitalDate(value?: Date | string | null) {
    if (!value) return "";
    const parts = getHospitalDateParts(value);
    return parts ? `${pad(parts.day)}/${pad(parts.month)}/${parts.year}` : "";
}

export function formatHospitalShortDate(value?: Date | string | null) {
    if (!value) return "";
    const parts = getHospitalDateParts(value);
    return parts ? `${pad(parts.day)}/${pad(parts.month)}` : "";
}

export function formatHospitalCompactDate(value?: Date | string | null) {
    if (!value) return "";
    const parts = getHospitalDateParts(value);
    return parts ? `${String(parts.year).slice(-2)}-${pad(parts.month)}-${pad(parts.day)}` : "";
}

export function formatHospitalDateTime(value?: Date | string | null) {
    if (!value) return "";
    const parts = getHospitalDateParts(value);
    return parts ? `${pad(parts.day)}/${pad(parts.month)}/${parts.year} ${pad(parts.hour)}:${pad(parts.minute)}` : "";
}

export function toHospitalDateInput(value?: Date | string | null) {
    return value ? getHospitalDateKey(value) : "";
}

export function toHospitalDateTimeInput(value?: Date | string | null) {
    const parts = value ? getHospitalDateParts(value) : null;
    return parts ? `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}` : "";
}

export function toHospitalDateTimeSecondInput(value?: Date | string | null) {
    const parts = value ? getHospitalDateParts(value) : null;
    return parts ? `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}:${pad(parts.second)}` : "";
}

export function toHospitalTimeInput(value?: Date | string | null) {
    const parts = value ? getHospitalDateParts(value) : null;
    return parts ? `${pad(parts.hour)}:${pad(parts.minute)}` : "";
}

export function parseHospitalTime(value: string, baseDate: Date) {
    const match = /^(\d{2}):(\d{2})$/.exec(value.trim());
    const base = getHospitalDateParts(baseDate);
    if (!match || !base) return null;
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    return hour <= 23 && minute <= 59 ? hospitalDateTimeToInstant(base.year, base.month, base.day, hour, minute) : null;
}

export function getHospitalDayWindow(now = new Date(), boundaryHour = 2) {
    const parts = getHospitalDateParts(now);
    if (!parts) throw new Error("Invalid hospital date");
    let start = hospitalDateTimeToInstant(parts.year, parts.month, parts.day, boundaryHour);
    if (!start) throw new Error("Invalid hospital boundary");
    if (now < start) start = new Date(start.getTime() - 24 * 60 * 60 * 1000);
    return { start, end: new Date(start.getTime() + 24 * 60 * 60 * 1000) };
}

export function getHospitalPeriodValue(now = new Date()) {
    return getHospitalDateKey(getHospitalDayWindow(now).start);
}

export function getNextHospitalBoundary(now = new Date(), boundaryHour = 2) {
    const { end } = getHospitalDayWindow(now, boundaryHour);
    return end;
}
